import { Box } from '@mui/material';
import PillBadge from 'components/atomic/organisms/pill-badge';
import { useDesignSystem } from 'design-system';
import { validateVariant } from 'ui/utils';
import { useImageCounter } from './use-image-counter';

export const IMAGE_COUNTER_VARIANTS = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
} as const;

export type ImageCounterVariant = typeof IMAGE_COUNTER_VARIANTS[keyof typeof IMAGE_COUNTER_VARIANTS];

export interface ImageCounterProps {
  /** Current image index (1-based) */
  current: number;
  /** Total number of images */
  total: number;
  /** Variant: mobile shows "1/33", desktop shows "See All X Photos" */
  variant: ImageCounterVariant;
  /** Click handler to open gallery */
  onClick?: () => void;
  /** Additional CSS class */
  className?: string;
}

/**
 * ImageCounter - Displays image count in carousel
 * 
 * @example
 * ```tsx
 * <ImageCounter current={1} total={33} variant="mobile" />
 * <ImageCounter current={1} total={33} variant="desktop" onClick={() => openGallery()} />
 * ```
 */
export function ImageCounter({
  current,
  total,
  variant,
  onClick,
  className,
}: ImageCounterProps) {
  const { components } = useDesignSystem();
  const { imageCounter } = components.badge;
  const { text, ariaLabel } = useImageCounter({ current, total, variant });

  if (!validateVariant('ImageCounter', variant, IMAGE_COUNTER_VARIANTS)) {
    return null;
  }

  const badge = (
    <PillBadge
      backgroundColor={imageCounter.bg}
      textColor={imageCounter.color}
      className={className}
      ariaLabel={ariaLabel}
    >
      {text}
    </PillBadge>
  );

  if (onClick) {
    return (
      <Box
        component="button"
        onClick={e => {
          e.stopPropagation();
          onClick();
        }}
        sx={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        {badge}
      </Box>
    );
  }

  return badge;
}

export default ImageCounter;
